import type { Map as MapType } from "mapbox-gl";
import moment from "moment/min/moment-with-locales";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  Icon,
  ListItem,
  Markdown,
  ModalButton,
  Tabs,
  useIsMobile,
} from "senf-atomic-design-system";
import {
  capitalizeFirstLetter,
  fixFalslyTranslatedMarkdown,
  formatBytes,
  openLink,
  useGlobalState,
} from "senf-shared";
import type {
  Project,
  SectionButton,
  SectionCalendar,
  SectionFAQ,
  SectionFile,
  SectionImage,
  SectionMap,
  SectionText,
  SectionTimeline,
} from "senf-shared/types";
import { cn } from "../../../util/classNameConstructor";
import MapViewEditor from "../../atoms/map/MapViewEditor";
import Button from "../../core/button/Button";
import Pagination from "../../core/pagination/Pagination";
import TruncateMiddle from "../../core/TruncateMiddle";
import useTable from "../../organisms/table/useTable";
import { CopyrightIcon } from "../copyright/CopyrightIcon";
import SortableLogos from "../survey/components/SortableLogos";
import { EventItem, EventThumbnail } from "./InfoPageBuilderComponents";

interface SectionProps<T> {
  section: T;
  project?: Project;
}

interface ImageData {
  url: string;
  alt?: string;
  copyright?: string;
  height?: number;
  width?: number;
  keepAspectRatio?: boolean;
}

export const TextSection = ({
  section,
  as: Component = "section",
}: SectionProps<SectionText> & { as?: keyof JSX.IntrinsicElements }) => {
  const [translation] = useGlobalState().translationsState;

  return (
    <Component
      className="flex flex-col gap-3"
      section-id={section.uid}
      {...(Component === "section" && {
        "aria-label": translation?.[section?.title] || section?.title,
      })} // Only add aria-label if it's a section
    >
      <h3 className="heading-2xl">
        {translation?.[section?.title] || section?.title}
      </h3>
      <p className="body-md">
        <Markdown>
          {fixFalslyTranslatedMarkdown(
            (section.body && translation?.[section.body]) || section.body,
          )}
        </Markdown>
      </p>
    </Component>
  );
};

export const MapSection = ({ section }: SectionProps<SectionMap>) => {
  const { t } = useTranslation();
  const [translation] = useGlobalState().translationsState;
  return (
    <section
      className="flex flex-col gap-5 pt-[15vh]"
      aria-label={translation?.[section.title] || section.title}
      section-id={section.uid}
    >
      <TextSection
        as="div"
        section={{
          ...section,
          title:
            section.title === null || section.title === undefined
              ? t("infoPage:faq.title")
              : translation?.[section.title] || section.title,
          body:
            section.body === null || section.body === undefined
              ? t("infoPage:faq.description")
              : translation?.[section.body] || section.body,
        }}
      />
    </section>
  );
};

export const LogoSection = ({ section }: SectionProps<SectionImage>) => {
  return (
    <section section-id={section.uid}>
      <SortableLogos mode="display" logos={section?.images} />
    </section>
  );
};

export const ImageSection = ({ section }: SectionProps<SectionImage>) => {
  const { t } = useTranslation();
  const [translation] = useGlobalState().translationsState;
  const isMobile = useIsMobile();

  return (
    <section
      className="grid gap-5"
      aria-label={t("global:images")}
      section-id={section.uid}
    >
      {section?.images?.map((image: ImageData) => {
        const { url, alt, copyright } = image;
        const isVideo = section.type === "video";

        return (
          <div className="relative" key={url}>
            {copyright && (
              <CopyrightIcon
                copyright={copyright}
                captionId={`figcaption-${url}`}
                className={cn(isVideo && "top-0 bottom-auto")}
              />
            )}

            {!isVideo && (
              <img
                src={url}
                alt={(alt && translation?.[alt]) || alt || t("global:image")}
                className={cn("rounded-xl")}
                aria-describedby={`figcaption-${url}`}
              />
            )}
            {isVideo && (
              <div className="relative">
                <video
                  className={cn("mx-auto rounded-xl bg-black w-full")}
                  controls
                  playsInline
                  webkit-playsinline="true"
                  x5-playsinline="true"
                  preload="auto"
                  aria-describedby={`figcaption-${url}`}
                >
                  <source src={url} type="video/mp4" />
                  <source src={url} type="video/quicktime" />
                  <track kind="captions" />
                  Your browser does not support the video tag.
                </video>
                {alt && (
                  <span className="sr-only">
                    {translation?.[alt] || alt || t("global:decorativeVideo")}
                  </span>
                )}
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
};

export const FileSection = ({ section }: SectionProps<SectionFile>) => {
  const { t } = useTranslation();
  if (!section.files?.length) return null;
  return (
    <section
      className="border-border-default flex flex-col rounded-2xl border p-1"
      aria-label={t("global:files")}
      section-id={section.uid}
    >
      {[...(section.files || [])].map((file, i) => {
        return (
          <ListItem
            key={file.name}
            index={i}
            primaryContent={<TruncateMiddle text={file.name} />}
            secondaryContent={formatBytes(file.size, { decimals: 1 })}
            leadingIcon="file/pdf-28"
            onClick={(e) => {
              e.preventDefault();
              window.open(file?.blobUrl || file?.url, "_blank");
            }}
          />
        );
      })}
    </section>
  );
};

export const FAQSection = ({ section }: SectionProps<SectionFAQ>) => {
  const { t } = useTranslation();
  const [translation] = useGlobalState().translationsState;

  if (!section?.faq) return null;
  return (
    <section
      className="flex flex-col gap-5"
      aria-label={translation?.[section.title] || section.title}
      section-id={section.uid}
    >
      <TextSection
        as="div"
        section={{
          ...section,
          title:
            section.title === null || section.title === undefined
              ? t("infoPage:faq.title")
              : translation?.[section.title] || section.title,
          body:
            section.body === null || section.body === undefined
              ? t("infoPage:faq.description")
              : translation?.[section.body] || section.body,
        }}
      />

      <Accordion
        data={section?.faq?.map((e) => ({
          stackTitle: translation?.[e.question] || e.question,
          stackContent: translation?.[e.answer] || e.answer,
        }))}
      />
    </section>
  );
};

export const ButtonSection = ({ section }: SectionProps<SectionButton>) => {
  const { t } = useTranslation();

  const isMobile = useIsMobile();
  const [translation] = useGlobalState().translationsState;

  return (
    <section
      className={cn("flex justify-start gap-5", isMobile ? "-my-7" : "-my-8")}
      aria-label={t("global:links")}
      section-id={section.uid}
    >
      {section?.buttons?.map(({ text, link, uid }) => {
        return (
          <Button
            text={translation?.[text] || text || link}
            key={uid}
            leadingIcon={link?.includes("@") ? "mail" : "link"}
            size="lg"
            variant="outline"
            onClick={() => {
              const formattedLink = link?.includes("@")
                ? `mailto:${link}`
                : link;
              openLink(formattedLink);
            }}
          />
        );
      })}
    </section>
  );
};

const EventDetails = ({ event }) => {
  const { t } = useTranslation();
  const [translation] = useGlobalState().translationsState;

  const startTime = moment(event.start.date).format("HH:mm");
  const endTime = moment(event.end.date).format("HH:mm");

  return (
    <section className="@container min-h-[320px]">
      <div className="items-between @lg:flex-row @lg:p-10 container flex flex-col justify-between gap-x-10 gap-y-5 p-5">
        <EventThumbnail event={event} />
        <div className="flex w-full flex-col gap-10">
          <div className="flex flex-col items-start gap-2">
            <span className="heading-3xl">
              {translation?.[event.summary] || event.summary}
            </span>
            <span className="body-md whitespace-pre-wrap">
              {translation?.[event.description] || event.description}
            </span>
            {event.url && (
              <Button
                variant="primary"
                leadingIcon={event.url.includes("mailto:") ? "mail" : undefined}
                trailingIcon={
                  event.url.includes("mailto:") ? undefined : "arrow-right-md"
                }
                text={
                  translation?.[event.xProps?.urlLabel] ||
                  event.xProps?.urlLabel
                }
                onClick={() => openLink(event.url)}
                classNameWrapper={
                  document.dir === "rtl" ? "ml-auto" : "mr-auto"
                }
              />
            )}
          </div>

          <div className="grid gap-5">
            <div className="bg-background-default grid grid-cols-2 gap-10 rounded-lg p-3 px-4">
              <div className="flex flex-col gap-1">
                <span className="body-md-bold">{t("global:date")}</span>
                <div className="flex items-start gap-1">
                  <Icon
                    name="calendar"
                    className="text-icon-default shrink-0"
                  />
                  <span className="">
                    {moment(event.start.date).format("DD. MMMM YYYY")}
                  </span>
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <span className="body-md-bold">{t("global:clockTime")}</span>
                <div className="flex items-start gap-1">
                  <Icon name="clock" className="text-icon-default shrink-0" />
                  <span className="">
                    {startTime === endTime
                      ? t("global:allDay")
                      : `${startTime} – ${endTime}`}
                  </span>
                </div>
              </div>
            </div>
            <div className="bg-background-default flex flex-col gap-1 rounded-lg p-3 px-4">
              <span className="body-md-bold">{t("global:eventLocation")}</span>
              <div className="flex flex-wrap items-center gap-3">
                {event.location && (
                  <span className="flex items-start gap-1">
                    <Icon name="pin" className="text-icon-default shrink-0" />
                    {event.location?.split(",").slice(0, 2).join(",")}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const CalendarSection = ({
  section,
  project,
}: SectionProps<SectionCalendar>) => {
  const { t, i18n } = useTranslation();
  const [translation] = useGlobalState().translationsState;

  moment.locale(i18n.resolvedLanguage);
  const [events] = useState([]); // Entfernen Sie setEvents, da es nicht verwendet wird

  const allEvents = useMemo(() => {
    return [...(section?.events || []).filter((e) => e), ...events].sort(
      (a, b) =>
        new Date(a.start.date).getTime() - new Date(b.start.date).getTime(),
    );
  }, [section?.events, events]);

  // Separate current and past events
  const currentEvents = useMemo(
    () => allEvents.filter((event) => new Date(event.start.date) >= new Date()),
    [allEvents],
  );

  const pastEvents = useMemo(
    () => allEvents.filter((event) => new Date(event.start.date) < new Date()),
    [allEvents],
  );

  const [activeTab, setActiveTab] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  const displayedEvents = useMemo(
    () =>
      currentEvents.length > 0 && pastEvents.length > 0
        ? activeTab === 1
          ? currentEvents
          : pastEvents
        : pastEvents.length > 0
          ? pastEvents
          : currentEvents,
    [currentEvents, pastEvents, activeTab],
  );

  const { slice, range } = useTable(displayedEvents, currentPage, 5);

  return (
    <>
      <section
        className="flex flex-col gap-2"
        aria-label={t("global:calendar")}
        section-id={section.uid}
      >
        {/* <TextSection
          as="div"
          section={{
            uid: section.uid,
            title: t("global:calendar"),
          }}
        /> */}
        <TextSection
          as="div"
          section={{
            ...section,
            title:
              section.title === null || section.title === undefined
                ? t("global:calendar")
                : translation?.[section.title] || section.title,
          }}
        />

        {/* Show Tabs only if there are events in both currentEvents and pastEvents */}
        {currentEvents.length > 0 && pastEvents.length > 0 && (
          <Tabs
            active={activeTab}
            setActive={setActiveTab}
            classNames={{ tab: "heading-lg" }}
            tabs={[
              { text: t("global:upcoming") }, // Tab for current events
              { text: t("global:past") }, // Tab for past events
            ]}
            withBorder
          />
        )}

        <div className="flex gap-10">
          <div className="-mt-2 flex w-full flex-col">
            {slice?.map((event, i) => {
              return (
                <div className="flex flex-col" key={i}>
                  {((slice[i - 1] &&
                    moment(slice[i - 1].start.date).format("YYYY-MM") !==
                      moment(event.start.date).format("YYYY-MM")) ||
                    i === 0) && (
                    <span className="heading-lg text-text-subdued mt-5 normal-case">
                      {capitalizeFirstLetter(
                        moment(event.start.date).format("MMMM YYYY"),
                      )}
                    </span>
                  )}
                  <ModalButton
                    key={event.uid}
                    button={EventItem}
                    event={event}
                    options={{ id: event.uid, size: "lg", zIndex: 999999 }}
                    className="cursor-pointer"
                  >
                    <EventDetails event={event} />
                  </ModalButton>
                </div>
              );
            })}
          </div>
        </div>
        {range?.length > 1 && (
          <Pagination
            range={range}
            slice={slice}
            setPage={setCurrentPage}
            page={currentPage}
            showPages={10}
          />
        )}
      </section>
    </>
  );
};

export const TimelineSection = ({ section }: SectionProps<SectionTimeline>) => {
  const { t } = useTranslation();
  const [translation] = useGlobalState().translationsState;

  if (!section?.timeline) return null;
  return (
    <section
      className="flex flex-col gap-5"
      aria-label={translation?.[section.title] || section.title}
      section-id={section.uid}
    >
      <TextSection
        as="div"
        section={{
          ...section,
          title:
            section.title === null || section.title === undefined
              ? t("global:timeline")
              : translation?.[section.title] || section.title,
          body:
            section.body === null || section.body === undefined
              ? t("infoPage:timeline.description")
              : translation?.[section.body] || section.body,
        }}
      />

      <div className="flex flex-col gap-5">
        {section.timeline.map((item, index) => (
          <div key={item.uid} className="flex gap-5">
            <div className="relative flex flex-col items-center top-1.5">
              <div className="bg-surface-highlight-default h-3 w-3 rounded-full" />
              {index < section.timeline.length - 1 && (
                <div className="bg-border-subdued absolute top-4 h-full w-0.5" />
              )}
            </div>
            <div className="flex flex-col gap-2 pb-10">
              <span className="heading-xl">
                {translation?.[item.title] || item.title}
              </span>
              <span className="body-md whitespace-pre-wrap">
                <Markdown>
                  {fixFalslyTranslatedMarkdown(
                    translation?.[item.description] || item.description,
                  )}
                </Markdown>
              </span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
